import type { PracticeAreaId } from './practice-areas';

export const SITUATIONS = [
  {
    id: 'family-crisis',
    emoji: '👨‍👩‍👧‍👦',
    label: 'My family situation is changing',
    sub: 'Divorce, custody, support, adoption, protection orders',
    color: '#E8475F',
    template: 'family-law-intake',
    areas: ['family-law'],
    matters: [
      {
        id: 'fam-001',
        label: 'Divorce (we disagree on terms)',
        formal: 'Divorce (Contested)',
      },
      {
        id: 'fam-002',
        label: 'Divorce (we agree on terms)',
        formal: 'Divorce (Uncontested)',
      },
      {
        id: 'fam-005',
        label: 'Child custody arrangement',
        formal: 'Child Custody',
      },
      {
        id: 'fam-006',
        label: 'Change an existing custody order',
        formal: 'Child Custody Modification',
      },
      { id: 'fam-007', label: 'Child support', formal: 'Child Support' },
      {
        id: 'fam-008',
        label: 'Enforce a child support order',
        formal: 'Child Support Enforcement',
      },
      {
        id: 'fam-009',
        label: 'Spousal support / alimony',
        formal: 'Spousal Support / Alimony',
      },
      {
        id: 'fam-011',
        label: 'Domestic violence / protection order',
        formal: 'Domestic Violence / Protective Order',
      },
      { id: 'fam-012', label: 'Establish paternity', formal: 'Paternity' },
      { id: 'fam-013', label: 'Adoption', formal: 'Adoption' },
      {
        id: 'fam-018',
        label: 'Prenuptial or postnuptial agreement',
        formal: 'Prenuptial / Postnuptial Agreement',
      },
    ],
    qualifiers: [
      { id: 'fam-q1', q: 'Are you currently married?', type: 'yesno' },
      { id: 'fam-q2', q: 'Do you have children under 18?', type: 'yesno' },
      {
        id: 'fam-q3',
        q: 'Is there an existing court case or order?',
        type: 'yesno',
      },
      { id: 'fam-q4', q: 'What county/state do you reside in?', type: 'text' },
    ],
  },
  {
    id: 'injured',
    emoji: '🏥',
    label: "I've been hurt or injured",
    sub: 'Car accidents, slip & fall, medical mistakes, workplace injuries',
    color: '#D97706',
    template: 'personal-injury-intake',
    areas: ['personal-injury', 'workers-compensation'],
    matters: [
      {
        id: 'pi-001',
        label: 'Car or vehicle accident',
        formal: 'Auto / Motor Vehicle Accident',
      },
      {
        id: 'pi-002',
        label: 'Truck or commercial vehicle accident',
        formal: 'Truck / Commercial Vehicle Accident',
      },
      {
        id: 'pi-004',
        label: 'Slip and fall / unsafe property',
        formal: 'Slip and Fall / Premises Liability',
      },
      {
        id: 'pi-005',
        label: 'Medical mistake or malpractice',
        formal: 'Medical Malpractice',
      },
      {
        id: 'pi-006',
        label: 'Defective or dangerous product',
        formal: 'Product Liability',
      },
      {
        id: 'pi-008',
        label: 'Someone died due to negligence',
        formal: 'Wrongful Death',
      },
      {
        id: 'pi-009',
        label: 'Dog bite or animal attack',
        formal: 'Dog Bite / Animal Attack',
      },
      {
        id: 'pi-015',
        label: 'Serious brain or spinal cord injury',
        formal: 'Brain / Spinal Cord Injury',
      },
      {
        id: 'pi-017',
        label: 'Rideshare (Uber/Lyft) accident',
        formal: 'Rideshare Accident',
      },
      {
        id: 'pi-019',
        label: 'Assault or intentional harm',
        formal: 'Assault / Intentional Tort',
      },
      {
        id: 'wc-001',
        label: "Hurt at work (workers' comp)",
        formal: "Workers' Comp — Initial Claim",
      },
      {
        id: 'wc-002',
        label: "Workers' comp claim denied",
        formal: "Workers' Comp — Denied Claim",
      },
    ],
    qualifiers: [
      {
        id: 'inj-q1',
        q: 'When did the injury occur?',
        type: 'text',
        placeholder: 'e.g. Last week, 3 months ago',
      },
      { id: 'inj-q2', q: 'Have you sought medical treatment?', type: 'yesno' },
      { id: 'inj-q3', q: 'Did the injury happen at work?', type: 'yesno' },
    ],
  },
  {
    id: 'trouble-law',
    emoji: '⚖️',
    label: "I'm in trouble with the law",
    sub: 'Arrested, charged, DUI, tickets, warrants, record clearing',
    color: '#7C3AED',
    template: 'criminal-defense-intake',
    areas: ['criminal-defense'],
    matters: [
      { id: 'crim-001', label: 'DUI / DWI', formal: 'DUI / DWI' },
      { id: 'crim-002', label: 'Drug charges', formal: 'Drug Offenses' },
      {
        id: 'crim-003',
        label: 'Assault or battery',
        formal: 'Assault / Battery',
      },
      {
        id: 'crim-004',
        label: 'Domestic violence (criminal)',
        formal: 'Domestic Violence (Criminal)',
      },
      {
        id: 'crim-005',
        label: 'Theft or shoplifting',
        formal: 'Theft / Larceny',
      },
      {
        id: 'crim-008',
        label: 'Fraud or white collar crime',
        formal: 'White Collar Crime',
      },
      {
        id: 'crim-010',
        label: 'Juvenile / minor arrested',
        formal: 'Juvenile Offenses',
      },
      {
        id: 'crim-012',
        label: 'Traffic ticket or moving violation',
        formal: 'Traffic / Moving Violations',
      },
      {
        id: 'crim-013',
        label: 'Clear my record (expungement)',
        formal: 'Expungement / Record Sealing',
      },
      {
        id: 'crim-014',
        label: 'Probation or parole violation',
        formal: 'Probation / Parole Violation',
      },
    ],
    qualifiers: [
      { id: 'crim-q1', q: 'Have you been arrested or charged?', type: 'yesno' },
      { id: 'crim-q2', q: 'Is there a court date scheduled?', type: 'yesno' },
      {
        id: 'crim-q3',
        q: 'Are you currently in custody or on bail?',
        type: 'yesno',
      },
      {
        id: 'crim-q4',
        q: 'Is this for yourself or someone else?',
        type: 'choice',
        options: ['Myself', 'A family member', 'Someone else'],
      },
    ],
  },
  {
    id: 'work-problem',
    emoji: '💼',
    label: 'I have a problem at work',
    sub: 'Fired unfairly, discrimination, harassment, unpaid wages',
    color: '#2563EB',
    template: 'business-intake',
    areas: ['employment-labor'],
    matters: [
      {
        id: 'emp-001',
        label: 'Fired unfairly (wrongful termination)',
        formal: 'Wrongful Termination',
      },
      {
        id: 'emp-002',
        label: 'Discrimination (race, gender, age, etc.)',
        formal: 'Employment Discrimination',
      },
      {
        id: 'emp-003',
        label: 'Sexual harassment',
        formal: 'Sexual Harassment',
      },
      {
        id: 'emp-004',
        label: 'Unpaid wages or overtime',
        formal: 'Wage and Hour Disputes',
      },
      {
        id: 'emp-005',
        label: 'Denied medical or family leave (FMLA)',
        formal: 'FMLA / Leave Issues',
      },
      {
        id: 'emp-006',
        label: 'Non-compete or restrictive covenant',
        formal: 'Non-Compete / Restrictive Covenant',
      },
      {
        id: 'emp-007',
        label: 'Retaliation / whistleblower',
        formal: 'Whistleblower / Retaliation',
      },
      {
        id: 'emp-009',
        label: 'Denied unemployment benefits',
        formal: 'Unemployment Benefits',
      },
      {
        id: 'emp-010',
        label: 'Unsafe workplace / OSHA',
        formal: 'Workplace Safety / OSHA',
      },
      {
        id: 'emp-011',
        label: 'Misclassified as contractor',
        formal: 'Employee Misclassification',
      },
    ],
    qualifiers: [
      { id: 'emp-q1', q: 'Are you currently employed there?', type: 'yesno' },
      { id: 'emp-q2', q: 'How long have/did you work there?', type: 'text' },
      {
        id: 'emp-q3',
        q: 'Have you filed a complaint with HR or a government agency?',
        type: 'yesno',
      },
    ],
  },
  {
    id: 'debt-money',
    emoji: '💰',
    label: "I'm drowning in debt",
    sub: 'Bankruptcy, collections, foreclosure, garnishment, tax debt',
    color: '#059669',
    template: 'bankruptcy-intake',
    areas: ['bankruptcy-debt', 'tax-law'],
    matters: [
      {
        id: 'bk-001',
        label: 'Chapter 7 bankruptcy (fresh start)',
        formal: 'Chapter 7 Bankruptcy',
      },
      {
        id: 'bk-002',
        label: 'Chapter 13 bankruptcy (repayment plan)',
        formal: 'Chapter 13 Bankruptcy',
      },
      {
        id: 'bk-005',
        label: 'Wages being garnished',
        formal: 'Wage Garnishment',
      },
      {
        id: 'bk-006',
        label: 'Creditors harassing me',
        formal: 'Creditor Harassment / FDCPA',
      },
      {
        id: 'bk-007',
        label: 'Negotiate or settle debts',
        formal: 'Debt Negotiation / Settlement',
      },
      {
        id: 'bk-008',
        label: 'Foreclosure defense (save my home)',
        formal: 'Foreclosure Defense',
      },
      {
        id: 'bk-009',
        label: 'Car being repossessed',
        formal: 'Repossession Defense',
      },
      {
        id: 'bk-011',
        label: 'Student loan debt',
        formal: 'Student Loan Discharge',
      },
      { id: 'tax-002', label: 'I owe the IRS', formal: 'IRS Tax Debt' },
      {
        id: 'tax-004',
        label: 'Tax lien or levy on my property',
        formal: 'Tax Lien / Levy Release',
      },
    ],
    qualifiers: [
      {
        id: 'debt-q1',
        q: 'What is your approximate total debt?',
        type: 'choice',
        options: ['Under $10K', '$10K-$50K', '$50K-$100K', '$100K-$500K', 'Over $500K'],
      },
      { id: 'debt-q2', q: 'Do you have regular income?', type: 'yesno' },
    ],
  },
  {
    id: 'property',
    emoji: '🏠',
    label: "I'm buying, selling, or renting property",
    sub: 'Real estate, landlord disputes, evictions, leases',
    color: '#0891B2',
    template: 'real-estate-intake',
    areas: ['real-estate', 'landlord-tenant'],
    matters: [
      {
        id: 're-001',
        label: 'Buying or selling a home',
        formal: 'Residential Purchase / Sale',
      },
      { id: 're-005', label: 'Commercial lease', formal: 'Commercial Lease' },
      {
        id: 're-003',
        label: 'Title or deed issue',
        formal: 'Title Examination',
      },
      { id: 're-009', label: 'HOA dispute', formal: 'HOA Disputes' },
      {
        id: 're-010',
        label: 'Boundary or easement dispute',
        formal: 'Boundary / Easement Disputes',
      },
      {
        id: 'lt-001',
        label: 'I need to evict a tenant',
        formal: 'Eviction (Landlord)',
      },
      {
        id: 'lt-002',
        label: "I'm being evicted",
        formal: 'Eviction Defense (Tenant)',
      },
      { id: 'lt-003', label: 'Lease dispute', formal: 'Lease Disputes' },
      {
        id: 'lt-004',
        label: 'Security deposit dispute',
        formal: 'Security Deposit Disputes',
      },
      {
        id: 'lt-005',
        label: 'Unsafe living conditions',
        formal: 'Habitability Issues',
      },
    ],
    qualifiers: [
      {
        id: 'prop-q1',
        q: 'Is this residential or commercial property?',
        type: 'choice',
        options: ['Residential', 'Commercial'],
      },
      {
        id: 'prop-q2',
        q: 'What state/county is the property in?',
        type: 'text',
      },
      {
        id: 'prop-q3',
        q: 'What is your timeline?',
        type: 'choice',
        options: [
          'Immediate (< 30 days)',
          '1-3 months',
          '3-6 months',
          '6+ months',
          'Just exploring',
        ],
      },
    ],
  },
  {
    id: 'future-planning',
    emoji: '📋',
    label: "I need to plan for my family's future",
    sub: 'Wills, trusts, power of attorney, probate, elder care',
    color: '#7C3AED',
    template: 'estate-planning-intake',
    areas: ['estate-planning-probate', 'elder-law'],
    matters: [
      {
        id: 'est-001',
        label: 'Create or update a will',
        formal: 'Last Will & Testament',
      },
      {
        id: 'est-002',
        label: 'Set up a trust',
        formal: 'Revocable Living Trust',
      },
      {
        id: 'est-004',
        label: 'Special needs trust',
        formal: 'Special Needs Trust',
      },
      {
        id: 'est-005',
        label: 'Power of attorney',
        formal: 'Power of Attorney',
      },
      {
        id: 'est-007',
        label: 'Healthcare directive / living will',
        formal: 'Advance Healthcare Directive',
      },
      {
        id: 'est-008',
        label: "Handle a deceased person's estate (probate)",
        formal: 'Probate Administration',
      },
      {
        id: 'est-009',
        label: 'Challenge or defend a will',
        formal: 'Will Contest',
      },
      {
        id: 'est-015',
        label: 'Guardianship or conservatorship',
        formal: 'Guardianship / Conservatorship',
      },
      {
        id: 'eld-001',
        label: 'Medicaid / nursing home planning',
        formal: 'Medicaid Planning',
      },
      {
        id: 'eld-005',
        label: 'Elder abuse or neglect',
        formal: 'Elder Abuse / Neglect',
      },
    ],
    qualifiers: [
      {
        id: 'est-q1',
        q: 'Is this for yourself or a family member?',
        type: 'choice',
        options: ['Myself', 'A family member'],
      },
      { id: 'est-q2', q: 'Has someone recently passed away?', type: 'yesno' },
      { id: 'est-q3', q: 'Is there an existing will or trust?', type: 'yesno' },
      {
        id: 'est-q4',
        q: 'What is the approximate value of the estate?',
        type: 'choice',
        options: ['Under $500K', '$500K-$1M', '$1M-$5M', 'Over $5M', 'Not sure'],
      },
      {
        id: 'est-q5',
        q: 'What are your primary goals?',
        type: 'textarea',
        placeholder: 'e.g. Protect assets, provide for minor children, minimize taxes',
      },
    ],
  },
  {
    id: 'business',
    emoji: '🏢',
    label: 'I need help with my business',
    sub: 'Starting, contracts, disputes, IP, compliance',
    color: '#4338CA',
    template: 'business-intake',
    areas: ['business-corporate', 'intellectual-property'],
    matters: [
      {
        id: 'bus-001',
        label: 'Start or form a business (LLC, Corp)',
        formal: 'Business Formation (LLC, Corp)',
      },
      {
        id: 'bus-002',
        label: 'Review or draft a contract',
        formal: 'Business Contracts',
      },
      {
        id: 'bus-003',
        label: 'Business partner dispute',
        formal: 'Partnership Disputes',
      },
      {
        id: 'bus-005',
        label: 'Buying or selling a business',
        formal: 'Mergers & Acquisitions',
      },
      {
        id: 'bus-006',
        label: 'Business lawsuit',
        formal: 'Commercial Litigation',
      },
      { id: 'bus-007', label: 'Franchise agreement', formal: 'Franchise Law' },
      {
        id: 'bus-010',
        label: 'HR / employment compliance',
        formal: 'Employment Compliance',
      },
      {
        id: 'bus-011',
        label: 'Cease and desist response',
        formal: 'Cease & Desist Response',
      },
      {
        id: 'ip-001',
        label: 'Trademark my brand or logo',
        formal: 'Trademark Registration',
      },
      {
        id: 'ip-002',
        label: 'Someone is using my brand/IP',
        formal: 'Trademark Disputes / Infringement',
      },
      {
        id: 'ip-005',
        label: 'Patent an invention',
        formal: 'Patent Application / Prosecution',
      },
    ],
    qualifiers: [
      { id: 'bus-q1', q: 'Do you have an existing business?', type: 'yesno' },
      {
        id: 'bus-q2',
        q: 'Is there an active dispute or lawsuit?',
        type: 'yesno',
      },
      {
        id: 'bus-q3',
        q: 'Are contracts or agreements involved?',
        type: 'yesno',
      },
    ],
  },
  {
    id: 'immigration',
    emoji: '🌎',
    label: 'I need help with immigration',
    sub: 'Visas, green cards, citizenship, deportation, DACA',
    color: '#0D9488',
    template: 'immigration-intake',
    areas: ['immigration'],
    matters: [
      {
        id: 'imm-001',
        label: 'Green card through family',
        formal: 'Family-Based Green Card',
      },
      {
        id: 'imm-002',
        label: 'Green card through employer',
        formal: 'Employment-Based Green Card',
      },
      {
        id: 'imm-003',
        label: 'Become a U.S. citizen',
        formal: 'Naturalization / Citizenship',
      },
      { id: 'imm-004', label: 'Work visa (H-1B)', formal: 'H-1B Visa' },
      {
        id: 'imm-010',
        label: 'Extend or change visa status',
        formal: 'Visa Extension / Change of Status',
      },
      {
        id: 'imm-013',
        label: 'Facing deportation',
        formal: 'Removal / Deportation Defense',
      },
      { id: 'imm-014', label: 'Seeking asylum', formal: 'Asylum' },
      { id: 'imm-016', label: 'DACA', formal: 'DACA' },
      {
        id: 'imm-017',
        label: 'Detained by ICE',
        formal: 'Immigration Detention',
      },
      {
        id: 'imm-019',
        label: 'Crime victim visa (U-Visa / VAWA)',
        formal: 'U-Visa / VAWA',
      },
    ],
    qualifiers: [
      {
        id: 'imm-q1',
        q: 'What is your current immigration status?',
        type: 'text',
      },
      {
        id: 'imm-q2',
        q: 'Do you have a pending visa or application?',
        type: 'yesno',
      },
      {
        id: 'imm-q3',
        q: 'Is this for you, a family member, or an employee?',
        type: 'choice',
        options: ['Myself', 'A family member', 'An employee'],
      },
      {
        id: 'imm-q4',
        q: 'What is your country of origin?',
        type: 'text',
      },
    ],
  },
  {
    id: 'sued-owed',
    emoji: '📩',
    label: "Someone owes me money or I'm being sued",
    sub: 'Lawsuits, disputes, collections, consumer complaints',
    color: '#B45309',
    template: 'general-civil-intake',
    areas: ['civil-litigation', 'consumer-protection'],
    matters: [
      { id: 'civ-001', label: 'Broken contract', formal: 'Breach of Contract' },
      {
        id: 'civ-002',
        label: 'Collect money owed to me',
        formal: 'Collections / Debt Recovery',
      },
      { id: 'civ-003', label: 'Property dispute', formal: 'Property Disputes' },
      {
        id: 'civ-004',
        label: 'Fraud or scam',
        formal: 'Fraud / Misrepresentation',
      },
      {
        id: 'civ-005',
        label: 'Defamation (lies damaging my reputation)',
        formal: 'Defamation',
      },
      { id: 'civ-008', label: 'Small claims court', formal: 'Small Claims' },
      {
        id: 'civ-013',
        label: 'Insurance denied my claim',
        formal: 'Insurance Disputes',
      },
      {
        id: 'cp-001',
        label: 'Company ripping me off',
        formal: 'Consumer Deceptive Practices',
      },
      {
        id: 'cp-002',
        label: 'Lemon car / defective vehicle',
        formal: 'Lemon Law',
      },
      {
        id: 'adr-001',
        label: 'Settle without going to court',
        formal: 'Mediation / Arbitration',
      },
    ],
    qualifiers: [
      {
        id: 'civ-q1',
        q: 'Are you the one suing or being sued?',
        type: 'choice',
        options: ['I want to sue', "I'm being sued", 'Exploring options'],
      },
      { id: 'civ-q2', q: 'Have court papers been filed?', type: 'yesno' },
      {
        id: 'civ-q3',
        q: 'What is the approximate dollar amount?',
        type: 'choice',
        options: ['Under $5K', '$5K-$25K', '$25K-$100K', '$100K-$500K', 'Over $500K'],
      },
    ],
  },
  {
    id: 'something-else',
    emoji: '❓',
    label: 'Something else',
    sub: 'Government, disability, military, tax, other',
    color: '#6B7280',
    template: 'general-civil-intake',
    areas: ['social-security-disability', 'administrative-government', 'military-veterans'],
    matters: [
      {
        id: 'ssd-001',
        label: 'Disability benefits (SSDI / SSI)',
        formal: 'SSDI / SSI Application or Appeal',
      },
      {
        id: 'mil-001',
        label: 'VA / military benefits',
        formal: 'VA Disability Benefits',
      },
      {
        id: 'edu-001',
        label: 'Special education / IEP',
        formal: 'Special Education / IEP',
      },
      {
        id: 'tax-001',
        label: 'IRS audit or tax dispute',
        formal: 'IRS Audit / Tax Dispute',
      },
      {
        id: 'adm-001',
        label: 'Government permit or license',
        formal: 'Government Licensing / Permits',
      },
      {
        id: 'adm-002',
        label: 'Appeal a government decision',
        formal: 'Administrative Appeals',
      },
    ],
    qualifiers: [
      {
        id: 'other-q1',
        q: 'Briefly describe your legal issue',
        type: 'textarea',
      },
      { id: 'other-q2', q: 'Is there a deadline or court date?', type: 'yesno' },
    ],
  },
] as const;

// Ensure every `areas` entry is a known PracticeAreaId. If any situation lists
// an id not in the canonical taxonomy, this fails at compile time.
type _AssertSituationAreas = (typeof SITUATIONS)[number]['areas'][number] extends PracticeAreaId
  ? true
  : never;
const _situationAreasOk: _AssertSituationAreas = true;
void _situationAreasOk;
